import React, { useState } from "react";
import { WrapperSeamless } from "../../layout-components";
import Table from "../../components/tables/table";
import { Button } from "@material-ui/core";
import { Link } from "react-router-dom";
import { useGetList } from "../../hooks";
import * as Api from "../../service";
import useStore from "../../context";
import { useTranslation } from "localization/translation";
import { Select } from "../../components";
import { dateFormatter } from "helpers/dateFormatter";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ConfirmActionModal from "./ConfirmActionModal";
import EditOrderModal from "./EditOrderModal";

const STATUS_OPTIONS = [
  { id: "draft", name: "draft" },
  { id: "awaiting_payment", name: "awaiting_payment" },
  { id: "pending", name: "pending" },
  { id: "processing", name: "processing" },
  { id: "succeeded", name: "succeeded" },
  { id: "failed", name: "failed" },
  { id: "cancelled", name: "cancelled" },
  { id: "expired", name: "expired" },
];

const MODE_OPTIONS = [
  { id: "manual", name: "manual" },
  { id: "webhook", name: "webhook" },
  { id: "poll", name: "poll" },
  { id: "redirect", name: "redirect" },
];

const CONFIRMABLE = new Set(["awaiting_payment", "pending", "processing"]);

const withOrder = (text, order) =>
  String(text || "").replace("{order}", order || "");

const parseMetadata = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value) || {};
  } catch (_err) {
    return {};
  }
};

export default function PaymentOrdersList() {
  const translation = useTranslation();
  const { setErrorMsg, setSuccessMsg, lang, setPaymentOrdersPendingCount } =
    useStore();

  const refreshPendingCount = () => {
    Api.paymentOrders
      .getPendingCount()
      .then((res) => setPaymentOrdersPendingCount(res?.count ?? 0))
      .catch(() => {});
  };
  const [query, setQuery] = useState({
    sort: "created_at",
    order: "desc",
    status: "awaiting_payment",
    confirmation_mode: "manual",
  });
  const [editForm, setEditForm] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [items, total, refresh, setItems] = useGetList(
    Api.paymentOrders.getAll,
    query
  );

  const tableItems = (items || []).map((item) => ({
    ...item,
    _id: item.public_id || item._id || item.id,
  }));

  const statusOptions = STATUS_OPTIONS.map((opt) => ({
    ...opt,
    name: translation[`payment_status_${opt.id}`] || opt.name,
  }));
  const modeOptions = MODE_OPTIONS.map((opt) => ({
    ...opt,
    name: translation[`payment_mode_${opt.id}`] || opt.name,
  }));

  const closePending = () => {
    if (!actionLoading) setPendingAction(null);
  };

  const openConfirmPaid = (item) => {
    setPendingAction({ type: "confirm", item });
  };

  const openDelete = (item) => {
    setPendingAction({ type: "delete", item });
  };

  const openEdit = (item) => {
    const metadata = parseMetadata(item.metadata);
    setEditForm({
      public_id: item.public_id,
      status: item.status || "",
      confirmation_mode: item.confirmation_mode || "",
      billing_period: item.billing_period || "",
      amount: item.amount ?? "",
      discount_amount: item.discount_amount ?? "",
      total_amount: item.total_amount ?? "",
      invoice_number: metadata.invoice_number || "",
      description: item.description || "",
    });
  };

  const openSaveConfirm = () => {
    if (!editForm?.public_id) return;
    setPendingAction({ type: "update", item: editForm });
  };

  const handlePendingConfirm = async () => {
    if (!pendingAction?.item?.public_id || actionLoading) return;
    const { type, item } = pendingAction;
    setActionLoading(true);
    try {
      if (type === "confirm") {
        await Api.paymentOrders.confirm(item.public_id, {});
        setSuccessMsg(translation.payment_confirmed_success);
      } else if (type === "delete") {
        await Api.paymentOrders.delete(item.public_id);
        setItems((prev) =>
          (prev || []).filter(
            (row) =>
              row.public_id !== item.public_id && String(row.id) !== String(item.id)
          )
        );
        setSuccessMsg(translation.payment_order_deleted);
      } else if (type === "update") {
        await Api.paymentOrders.update(item.public_id, {
          status: item.status || undefined,
          confirmation_mode: item.confirmation_mode || undefined,
          billing_period: item.billing_period || undefined,
          amount: item.amount === "" ? undefined : Number(item.amount),
          discount_amount:
            item.discount_amount === "" ? undefined : Number(item.discount_amount),
          total_amount:
            item.total_amount === "" ? undefined : Number(item.total_amount),
          invoice_number: item.invoice_number,
          description: item.description,
        });
        setSuccessMsg(translation.payment_order_updated);
        setEditForm(null);
      }
      setPendingAction(null);
      refresh();
      refreshPendingCount();
    } catch (err) {
      setErrorMsg(err?.message || translation.something_went_wrong);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmCopy = (() => {
    const orderId = pendingAction?.item?.public_id;
    if (pendingAction?.type === "delete") {
      return {
        title: translation.delete_entry,
        text: withOrder(translation.delete_payment_order_prompt, orderId),
        confirmLabel: translation.delete,
        danger: true,
      };
    }
    if (pendingAction?.type === "update") {
      return {
        title: translation.are_you_sure,
        text: withOrder(translation.save_payment_order_prompt, orderId),
        confirmLabel: translation.save,
        danger: false,
      };
    }
    return {
      title: translation.are_you_sure,
      text: withOrder(translation.confirm_payment_prompt, orderId),
      confirmLabel: translation.mark_as_paid,
      danger: false,
    };
  })();

  const planName = (item) =>
    item[`plan_name_${lang}`] ||
    item.plan_name_en ||
    item.plan_name_hy ||
    item.plan_name_ru ||
    (item.plan_id ? `#${item.plan_id}` : "—");

  const rows = tableItems.map((item) => (
    <tr
      key={item.public_id}
      className="px-2 py-2 m-2 my-3 font-weight-bold"
    >
      <td className="text-left px-4">
        <div className="font-size-sm font-weight-bold">{item.public_id}</div>
        {item.metadata?.invoice_number && (
          <div className="font-size-xs text-black-50">
            {translation.invoice}: {item.metadata.invoice_number}
          </div>
        )}
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {[item.user_name, item.user_surname].filter(Boolean).join(" ") || "—"}
        </div>
        <div className="font-size-xs text-black-50">{item.user_email || "—"}</div>
        {item.user_id && (
          <Link
            to={`/user-details?userId=${item.user_id}`}
            className="font-size-xs"
          >
            #{item.user_id}
          </Link>
        )}
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">{planName(item)}</div>
        <div className="font-size-xs text-black-50">
          {item.billing_period || "—"}
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm font-weight-bold">
          {item.total_amount} {item.currency_code || ""}
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">{item.status}</div>
        <div className="font-size-xs text-black-50">
          {item.confirmation_mode}
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {item.created_at ? dateFormatter(item.created_at) : "—"}
        </div>
      </td>
      <td className="text-right px-4" style={{ whiteSpace: "nowrap" }}>
        <div className="d-flex align-items-center justify-content-end flex-nowrap">
          <div>
            <Button
              onClick={() => openEdit(item)}
              className="btn-neutral-first mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center"
            >
              <EditIcon color="primary" />
            </Button>
          </div>
          <div>
            <Button
              onClick={() => openDelete(item)}
              className="btn-neutral-danger mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center"
            >
              <DeleteIcon color="error" />
            </Button>
          </div>
          <div>
            {CONFIRMABLE.has(item.status) && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                className="mr-1 text-nowrap"
                onClick={() => openConfirmPaid(item)}
              >
                {translation.mark_as_paid}
              </Button>
            )}
          </div>
          {item.status === "succeeded" && (
            <span className="font-size-sm text-success px-2">
              {translation.paid}
            </span>
          )}
        </div>
      </td>
    </tr>
  ));

  return (
    <WrapperSeamless>
      <Table
        title={translation.payment_orders}
        noDrag
        userPage
        actions={false}
        items={tableItems}
        setItems={setItems}
        searchable
        searchHandler={(search) =>
          setQuery((prev) => ({
            ...prev,
            search: search || undefined,
          }))
        }
        total={total}
        header={
          <div className="table-header-filters d-flex flex-wrap">
            <Select
              onChange={(val) =>
                setQuery((st) => {
                  const next = { ...st };
                  if (val) next.status = val;
                  else delete next.status;
                  return { ...next };
                })
              }
              value={query.status || ""}
              field="name"
              label={translation.status}
              options={statusOptions}
            />
            <Select
              onChange={(val) =>
                setQuery((st) => {
                  const next = { ...st };
                  if (val) next.confirmation_mode = val;
                  else delete next.confirmation_mode;
                  return { ...next };
                })
              }
              value={query.confirmation_mode || ""}
              field="name"
              label={translation.confirmation_mode}
              options={modeOptions}
            />
          </div>
        }
        fields={
          <>
            <th className="text-left px-4">
              <span className="mr-3">{translation.order}</span>
            </th>
            <th className="text-left px-4">
              <span className="mr-3">{translation.user || "User"}</span>
            </th>
            <th className="text-left px-4">
              <span className="mr-3">{translation.plan}</span>
            </th>
            <th className="text-left px-4">
              <span className="mr-3">{translation.amount}</span>
            </th>
            <th className="text-left px-4">
              <span className="mr-3">{translation.status}</span>
            </th>
            <th className="text-left px-4">
              <span className="mr-3">{translation.created_at || "Created"}</span>
            </th>
            <th className="text-right px-4">
              <span className="mr-3">{translation.actions || "Actions"}</span>
            </th>
          </>
        }
        rows={rows}
      />
      <EditOrderModal
        open={!!editForm}
        form={editForm}
        statusOptions={statusOptions}
        modeOptions={modeOptions}
        onChange={(field, value) =>
          setEditForm((prev) => ({ ...prev, [field]: value }))
        }
        onClose={() => !actionLoading && setEditForm(null)}
        onSave={openSaveConfirm}
      />
      <ConfirmActionModal
        open={!!pendingAction}
        onClose={closePending}
        onConfirm={handlePendingConfirm}
        title={confirmCopy.title}
        text={confirmCopy.text}
        confirmLabel={confirmCopy.confirmLabel}
        danger={confirmCopy.danger}
        loading={actionLoading}
      />
    </WrapperSeamless>
  );
}
